import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Switch, TextField, Typography } from '@mui/material';
import {
  NEWSLETTER_CONSENT_VERSION,
  getNewsletterPreferences,
  hasPendingNewsletterOptIn,
  retryNewsletterOptIn,
  subscribeEmailToNewsletter,
  updateNewsletterPreferences,
} from '../lib/newsletter';
import { PrimaryButton, SubScreenHeader, YouCard } from './YouPrimitives';
import SignUpSheet from './SignUpSheet';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  flex: 1,
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    color: 'white',
    fontSize: '1rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--accent)' },
  },
  '& .MuiOutlinedInput-input': {
    height: 'auto',
    padding: '10px 14px',
    lineHeight: 1.5,
  },
  '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.45)', opacity: 1 },
};

const honeypotSx = {
  position: 'absolute',
  left: '-10000px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  opacity: 0,
};

function GuestNewsletterForm({ onCreateAccount }) {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (status === 'sending') return;
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      await subscribeEmailToNewsletter(value, website);
      if (!mountedRef.current) return;
      setEmail('');
      setStatus('done');
    } catch {
      if (!mountedRef.current) return;
      setStatus('idle');
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <Typography sx={{ color: '#fff', fontSize: 17, fontWeight: 600, mb: 0.75 }}>
        Guitar tips and upScale news
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Get guitar tips, new features and news from upScale by email.
      </Typography>
      {status === 'done' ? (
        <Alert severity="success">You're in. Welcome to the upScale newsletter.</Alert>
      ) : (
        <Box component="form" noValidate onSubmit={submit} sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              type="email"
              size="small"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="email"
              disabled={status === 'sending'}
              inputProps={{ 'aria-label': 'Email address' }}
              sx={{ ...fieldSx, flexBasis: 200 }}
            />
            <PrimaryButton
              type="submit"
              fullWidth={false}
              disabled={status === 'sending'}
              sx={{ height: 'auto', minWidth: 110 }}
            >
              {status === 'sending' ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Subscribe'}
            </PrimaryButton>
          </Box>
          <Box aria-hidden="true" sx={honeypotSx}>
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, mt: 1 }}>
            No spam, unsubscribe anytime.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      )}
      <Box sx={{ mt: 2.5 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 1.5 }}>
          With an account, you can manage your subscription right here in the app.
        </Typography>
        <Button
          variant="text"
          onClick={onCreateAccount}
          sx={{ textTransform: 'none', color: 'var(--accent)', px: 0, mt: 0.5 }}
        >
          Create an account
        </Button>
      </Box>
    </>
  );
}

function initialView(identity, signedIn, userId) {
  return {
    identity,
    preferences: null,
    loading: Boolean(signedIn),
    saving: false,
    error: null,
    pendingOptIn: Boolean(signedIn && hasPendingNewsletterOptIn(userId)),
  };
}

export default function NewsletterSection({ signedIn, userId, onBack }) {
  const identity = signedIn ? userId || '' : 'guest';
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const operationRef = useRef(0);
  const mountedRef = useRef(true);
  const [view, setView] = useState(() => initialView(identity, signedIn, userId));
  const [sheetOpen, setSheetOpen] = useState(false);
  const current = view.identity === identity ? view : initialView(identity, signedIn, userId);

  const commit = useCallback((operation, expectedIdentity, change) => {
    if (
      mountedRef.current &&
      operation === operationRef.current &&
      identityRef.current === expectedIdentity
    ) {
      setView((previous) =>
        change(
          previous.identity === expectedIdentity
            ? previous
            : initialView(expectedIdentity, signedIn, userId)
        )
      );
    }
  }, [signedIn, userId]);

  const load = useCallback(async () => {
    const expectedIdentity = identity;
    const operation = ++operationRef.current;
    setView(initialView(expectedIdentity, signedIn, userId));
    if (!signedIn) return;
    try {
      const preferences = await getNewsletterPreferences(userId);
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        preferences,
        loading: false,
        pendingOptIn: hasPendingNewsletterOptIn(userId),
      }));
    } catch (err) {
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        preferences: null,
        loading: false,
        error: err?.message || 'Newsletter preferences are unavailable.',
      }));
    }
  }, [commit, identity, signedIn, userId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
    };
  }, [load]);

  const toggle = async () => {
    if (!current.preferences || current.saving) return;
    const subscribed = !current.preferences.subscribed;
    const expectedIdentity = identity;
    const operation = ++operationRef.current;
    setView((previous) => ({ ...previous, saving: true, error: null }));
    try {
      const preferences = await updateNewsletterPreferences(
        subscribed,
        subscribed ? NEWSLETTER_CONSENT_VERSION : undefined,
        userId
      );
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        preferences,
        saving: false,
      }));
    } catch (err) {
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        saving: false,
        error: err?.message || 'We could not save your newsletter preference.',
      }));
    }
  };

  const retryOptIn = async () => {
    const expectedIdentity = identity;
    const operation = ++operationRef.current;
    setView((previous) => ({ ...previous, saving: true, error: null }));
    try {
      const preferences = await retryNewsletterOptIn(userId);
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        preferences: preferences || previous.preferences,
        saving: false,
        pendingOptIn: false,
      }));
    } catch (err) {
      commit(operation, expectedIdentity, (previous) => ({
        ...previous,
        saving: false,
        pendingOptIn: true,
        error: err?.message || 'We could not save your newsletter preference.',
      }));
    }
  };

  return (
    <>
      <SubScreenHeader title="Newsletter" onBack={onBack} />
      <YouCard>
        {signedIn && current.pendingOptIn && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={retryOptIn} disabled={current.saving}>
                Try again
              </Button>
            }
            sx={{ mb: 2 }}
          >
            Your newsletter signup has not been saved yet.
          </Alert>
        )}
        {!signedIn ? (
          <GuestNewsletterForm onCreateAccount={() => setSheetOpen(true)} />
        ) : current.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: 'var(--accent)' }} />
          </Box>
        ) : current.preferences ? (
          <>
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, lineHeight: 1.5, mb: 2 }}>
              Get guitar tips, new features and news from upScale by email. Unsubscribe whenever you like.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  {current.preferences.subscribed ? 'Subscribed' : 'Not subscribed'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5, overflowWrap: 'anywhere' }}>
                  {current.preferences.email}
                </Typography>
              </Box>
              {current.saving ? (
                <CircularProgress size={22} sx={{ color: 'var(--accent)', mr: 1.5 }} />
              ) : (
                <Switch
                  checked={Boolean(current.preferences.subscribed)}
                  onChange={toggle}
                  inputProps={{ 'aria-label': 'Newsletter subscription' }}
                />
              )}
            </Box>
          </>
        ) : (
          <Button fullWidth variant="outlined" onClick={load}>Try again</Button>
        )}
        {current.error && <Alert severity="error" sx={{ mt: 2 }}>{current.error}</Alert>}
      </YouCard>
      <SignUpSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}
